import React, { useEffect, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { call } from './api';
import { getActingFor, onActingForChange, setActingFor } from './actingFor';

/** shown above the Approval Inbox when someone has delegated their approvals to the user */
const ActingForBar: React.FC = () => {
  const [who, setWho] = useState<string | null>(getActingFor());
  useEffect(() => onActingForChange(() => setWho(getActingFor())), []);
  const delegated = useQuery({
    queryKey: ['actingFor'],
    queryFn: () => call<{ REQ_ID: number; DELEGATOR: string; DELEGATOR_NAME: string }[]>('delegated').then((r) => r.result ?? []),
    staleTime: 60_000,
  });
  const people = new Map<string, { name: string; n: number }>();
  (delegated.data ?? []).forEach((r) => {
    const p = people.get(r.DELEGATOR) ?? { name: r.DELEGATOR_NAME ?? r.DELEGATOR, n: 0 };
    p.n += 1;
    people.set(r.DELEGATOR, p);
  });
  // a delegation that ended or was cancelled: back to own approvals
  useEffect(() => {
    if (who && delegated.data && !people.has(who)) setActingFor(null);
  }, [who, delegated.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!people.size) return null;
  const change = (v: string | null) => {
    setActingFor(v === 'ME' ? null : v);
    // the inbox screens load on open: reload so they fetch for the chosen person
    window.location.reload();
  };
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: '12px', bgcolor: who ? '#fff7ed' : '#f0fdf4', border: `1px solid ${who ? '#fed7aa' : '#bbf7d0'}` }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <SwapHorizRoundedIcon sx={{ color: who ? '#c2410c' : '#15803d' }} />
          <Typography fontSize={14} fontWeight={600}>
            {who ? `Approving for ${people.get(who)?.name ?? who}` : 'Colleagues have delegated their approvals to you'}
          </Typography>
        </Stack>
        <ToggleButtonGroup exclusive size="small" value={who ?? 'ME'} onChange={(_, v) => v && change(v)}>
          <ToggleButton value="ME" sx={{ textTransform: 'none' }}>
            My approvals
          </ToggleButton>
          {[...people.entries()].map(([code, p]) => (
            <ToggleButton key={code} value={code} sx={{ textTransform: 'none' }}>
              {p.name} ({p.n})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
    </Box>
  );
};

export default ActingForBar;
